import { PureComponent } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';

import SelectType from '../components/SelectType';
import ExpressionSelector from './ExpressionSelector/ExpressionSelector';
import getArgumentType from 'src/utils/getArgumentType';
import type { ExpressionComponent } from '../utils/utils';
import type { FormulaNode, UiFormulaNode } from '../typeDefs';
import type { RootState } from '../redux/store';

interface CallExpressionProps {
    data: UiFormulaNode;
    functionPath?: string | null;
}

class CallExpression extends PureComponent<CallExpressionProps> {
    static getStub([$value = null]: [unknown?] = []): FormulaNode { return { $op: 'call', $value, $args: [] }; }

    handleFunctionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { data: { change } } = this.props;
        change({
            $value: event.target.value,
            $args: [],
        });
    };

    renderArg = ({ name, type }: { name: string; type: string }, index: number) => {
        const { data: { $args = [], rootRef, change } } = this.props;
        const props = {
            key: name + type + index,
            value: $args[index],
            onChange: (value: { type: string; value?: unknown }) => {
                const old = $args[index];
                if (old && old.type === value.type && old.value === value.value) return;
                return change('$args.' + index, value);
            },
        };

        const item = getArgumentType(type as Parameters<typeof getArgumentType>[0]);

        const getOptions = () => {
            const { key, value } = item;
            const extra: string[] = _.get(rootRef, `varTypes[${key}]`, []);
            return [
                ...extra.map(varName => [varName, 'args.' + varName, 'Variables']),
                ...(value || []),
            ].map(([optValue, label, group]: unknown[]) => ({
                value: optValue,
                label,
                group,
            }));
        };

        switch (item.type) {
            // new
            case 'enum': return <SelectType {...props} options={getOptions()} />;
            case 'number':
                return (
                    <SelectType
                        {...props}
                        options={getOptions()}
                        rawOption={true}
                        renderRawOption={renderNumberInput}
                    />
                );
        }

        throw new Error(`Invalid arg type: '${item.type}'`);
    };

    render() {
        const { functionPath, data: { state, $value, depth } } = this.props;

        if (!state.hidden && !functionPath) throw new Error(`Invalid function: ${$value}`);

        return <div className='expression-call'>
            {state.hidden ? null :
                <ExpressionSelector depth={depth + 1} path={functionPath} />
            }
        </div>;
    }
}

const renderNumberInput = ({ onChange, defaultValue }: { onChange: (value: number) => void; defaultValue?: unknown }) =>
    <input
        key={String(defaultValue)}
        type='number'
        onBlur={event => onChange(parseFloat(event.target.value))}
        defaultValue={defaultValue as string | number | undefined}
        style={{ maxWidth: '3rem', marginLeft: '.5rem' }}
    />;

export default connect((state: RootState, { data }: { data: UiFormulaNode }) => ({
    functionPath: data.state.hidden ? null :
        data.log.hasLog && data.state.nestedPath ? data.state.nestedPath :
        'functions.' + _.findIndex(state.formula.data.functions, { id: data.$value }) + '.structure',
}))(CallExpression) as unknown as ExpressionComponent;
